import { useState } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { inputCls, labelCls, ErrorNote, API_URL, fetchJson, fmt } from './shared';
import { MarketButtons } from '../MarketButtons';

const Field = ({ label, value, onChange, testId, placeholder }) => (
  <div>
    <label className={labelCls}>{label}</label>
    <input type="number" inputMode="decimal" min="0" step="any" value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} className={`${inputCls} font-mono`} data-testid={testId} />
  </div>
);

const calc = ({ capital, riskPct, entry, stop, target, direction }) => {
  const [c, r, e, s] = [capital, riskPct, entry, stop].map(Number);
  if (!(c > 0 && r > 0 && e > 0 && s > 0)) return { error: null, result: null };
  if (r > 100) return { error: 'Risk per position cannot exceed 100% of capital.' };
  const perUnit = direction === 'long' ? e - s : s - e;
  if (perUnit <= 0) return { error: direction === 'long' ? 'For a long position the stop level must be below the entry price.' : 'For a short position the stop level must be above the entry price.' };
  const budget = c * (r / 100);
  const units = Math.floor(budget / perUnit);
  const value = units * e;
  const t = Number(target);
  const rewardPerUnit = t > 0 ? (direction === 'long' ? t - e : e - t) : null;
  return {
    result: {
      budget, perUnit, units, value,
      actualRisk: units * perUnit,
      pctCapital: (value / c) * 100,
      stopPct: (perUnit / e) * 100,
      rr: rewardPerUnit && rewardPerUnit > 0 ? rewardPerUnit / perUnit : null,
    },
  };
};

const PRESET_KEY = 'tpmi_risk_preset';
const loadPreset = () => {
  try { return JSON.parse(localStorage.getItem(PRESET_KEY)) || null; } catch { return null; }
};

export const PositionRisk = ({ ready }) => {
  const [preset, setPreset] = useState(loadPreset);
  const [form, setForm] = useState(() => ({ capital: preset?.capital || '', riskPct: preset?.riskPct || '1', entry: '', stop: '', target: '', direction: 'long' }));
  const [lookup, setLookup] = useState({ market: '', ticker: '', loading: false, error: null, atr: null });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const { error, result } = calc(form);
  const canSave = Number(form.capital) > 0 && Number(form.riskPct) > 0 && Number(form.riskPct) <= 100;
  const savePreset = () => {
    const p = { capital: form.capital, riskPct: form.riskPct };
    localStorage.setItem(PRESET_KEY, JSON.stringify(p)); setPreset(p); toast.success('Defaults saved on this device');
  };
  const clearPreset = () => { localStorage.removeItem(PRESET_KEY); setPreset(null); toast.success('Saved defaults cleared'); };

  const loadPrice = async () => {
    setLookup((l) => ({ ...l, loading: true, error: null }));
    try {
      const q = new URLSearchParams({ ticker: lookup.ticker.trim(), market: lookup.market });
      const d = await fetchJson(`${API_URL}/api/tools/volatility-range?${q}`);
      setForm((f) => ({ ...f, entry: String(d.current_price) }));
      setLookup((l) => ({ ...l, loading: false, atr: d.atr_14 }));
    } catch (e) { setLookup((l) => ({ ...l, loading: false, error: e.message, atr: null })); }
  };

  return (
    <div className="space-y-5" data-testid="position-risk-tool">
      <div className="flex gap-2" role="radiogroup">
        {['long', 'short'].map((d) => (
          <button key={d} onClick={() => set('direction')(d)} data-testid={`direction-${d}-btn`}
            className={`text-xs px-4 py-1.5 rounded-full border transition-colors capitalize ${form.direction === d ? 'border-brand text-brand bg-brand/10' : 'border-zinc-800 text-zinc-500 hover:text-white'}`}>
            {d} position
          </button>
        ))}
      </div>

      <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-md p-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div className="sm:col-span-2">
          <label className={labelCls}>Prefill from market (optional)</label>
          <MarketButtons value={lookup.market} onChange={(v) => setLookup((l) => ({ ...l, market: v }))} prefix="risk" compact />
        </div>
        <div>
          <label className={labelCls}>Ticker</label>
          <input value={lookup.ticker} onChange={(e) => setLookup((l) => ({ ...l, ticker: e.target.value.toUpperCase() }))} placeholder="Ticker" maxLength={10}
          className={`${inputCls} font-mono`} data-testid="risk-ticker-input" />
        </div>
        <button onClick={loadPrice} disabled={!ready || !lookup.market || !lookup.ticker.trim() || lookup.loading}
          className="border border-zinc-700 hover:border-brand/60 text-zinc-300 text-xs px-4 py-2.5 rounded-md transition-colors disabled:opacity-30" data-testid="risk-load-price-btn">
          {lookup.loading ? 'Loading...' : 'Load last price'}
        </button>
      </div>
      <ErrorNote error={lookup.error} testId="risk-lookup-error" />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="Capital" value={form.capital} onChange={set('capital')} testId="risk-capital-input" placeholder="10000" />
        <Field label="Risk per position %" value={form.riskPct} onChange={set('riskPct')} testId="risk-pct-input" placeholder="1" />
        <Field label="Entry price" value={form.entry} onChange={set('entry')} testId="risk-entry-input" placeholder="50.00" />
        <Field label="Stop level" value={form.stop} onChange={set('stop')} testId="risk-stop-input" placeholder="47.50" />
        <Field label="Target (optional)" value={form.target} onChange={set('target')} testId="risk-target-input" placeholder="56.00" />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <button onClick={savePreset} disabled={!canSave} data-testid="risk-save-preset-btn"
          className="border border-zinc-700 hover:border-brand/60 text-zinc-300 px-3 py-1.5 rounded-md disabled:opacity-30 inline-flex items-center gap-1.5">
          <Save className="w-3.5 h-3.5" /> Save capital &amp; risk % as my defaults
        </button>
        {preset && (
          <>
            <span className="text-zinc-600" data-testid="risk-preset-status">Defaults: {fmt(preset.capital, 0)} at {preset.riskPct}%</span>
            <button onClick={clearPreset} className="text-zinc-500 hover:text-white underline" data-testid="risk-clear-preset-btn">Clear</button>
          </>
        )}
      </div>
      <ErrorNote error={error} testId="risk-error" />

      {result ? <RiskResults r={result} atr={lookup.atr} /> : !error && (
        <p className="text-xs text-zinc-600" data-testid="risk-empty">Enter capital, risk %, entry and stop to see the figures.</p>
      )}
    </div>
  );
};

const Out = ({ label, value, testId, accent }) => (
  <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-md p-3">
    <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</p>
    <p className={`font-mono text-sm ${accent ? 'text-brand' : ''}`} data-testid={testId}>{value}</p>
  </div>
);

const RiskResults = ({ r, atr }) => (
  <div className="space-y-3 animate-fade-in" data-testid="risk-results">
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <Out label="Units" value={r.units.toLocaleString()} testId="risk-units" accent />
      <Out label="Position value" value={fmt(r.value)} testId="risk-position-value" />
      <Out label="% of capital" value={`${fmt(r.pctCapital, 1)}%`} testId="risk-pct-capital" />
      <Out label="Risk budget" value={fmt(r.budget)} testId="risk-budget" />
      <Out label="Loss at stop" value={fmt(r.actualRisk)} testId="risk-actual" />
      <Out label="Stop distance" value={`${fmt(r.perUnit)} (${fmt(r.stopPct, 1)}%)`} testId="risk-stop-distance" />
      {r.rr && <Out label="Reward : risk" value={`${fmt(r.rr, 2)} : 1`} testId="risk-rr" />}
      {atr && <Out label="Stop distance vs ATR" value={`${fmt(r.perUnit / atr, 2)}× ATR`} testId="risk-atr-multiple" />}
    </div>
    {r.units === 0 && <p className="text-xs text-yellow-400" data-testid="risk-zero-units">The risk budget is smaller than the loss on one unit at this stop distance.</p>}
    {r.pctCapital > 100 && <p className="text-xs text-yellow-400" data-testid="risk-leverage-note">Position value exceeds the capital entered.</p>}
    <p className="text-[11px] text-zinc-600">Arithmetic only, based on the numbers you enter. Not a recommendation to trade.</p>
  </div>
);
